import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { Copy, Rocket, Loader2, Download, ExternalLink } from "lucide-react";
import { api, API, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SelectField, TextField } from "@/components/editor/fields";

const CodeBox = ({ code, testid }) => (
  <div className="relative">
    <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-all rounded-xl neu-inset p-3 pr-10 font-mono text-[11px] text-[#334155] thin-scroll" data-testid={testid}>{code}</pre>
    <button onClick={() => { navigator.clipboard.writeText(code); toast.success("Copied"); }} aria-label="Copy" data-testid={`${testid}-copy`} className="absolute right-2 top-2 rounded p-1.5 text-[#64748B] hover:bg-[#E2E8F3] hover:text-[#0F172A]"><Copy className="h-3.5 w-3.5" /></button>
  </div>
);

const Tab = ({ v, children }) => (
  <TabsTrigger value={v} data-testid={`publish-tab-${v}`} className="rounded-none border-b-2 border-transparent bg-transparent px-0 pb-2 text-xs text-[#64748B] data-[state=active]:border-[#3D3DF0] data-[state=active]:bg-transparent data-[state=active]:text-[#0F172A] data-[state=active]:shadow-none">{children}</TabsTrigger>
);

export const PublishDialog = ({ open, onOpenChange, book, onBookChange, beforePublish }) => {
  const [busy, setBusy] = useState(false);
  const [versions, setVersions] = useState([]);
  const qr = useRef();
  const origin = window.location.origin;
  const readUrl = `${origin}/read/${book.slug}`;
  const shareUrl = `${API}/share/${book.slug}`;
  const embed = `<iframe src="${origin}/embed/${book.slug}" width="100%" height="640" style="border:0" allow="autoplay; fullscreen" allowfullscreen title="${book.title}"></iframe>`;
  const wix = `<script src="${API}/public/wix-element.js" defer></script>\n<ibookstudio-book book="${book.slug}" height="640px"></ibookstudio-book>`;

  useEffect(() => {
    if (open) api.get(`/books/${book.book_id}/versions`).then((r) => setVersions(r.data));
  }, [open, book.book_id, book.published_version]);

  const saveSettings = async (patch) => {
    const { data } = await api.patch(`/books/${book.book_id}`, { settings: patch });
    onBookChange({ settings: data.settings });
  };

  const publish = async () => {
    setBusy(true);
    try {
      await beforePublish();
      const { data } = await api.post(`/books/${book.book_id}/publish`);
      onBookChange({ published_version: data.version });
      toast.success(`Published version ${data.label}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const downloadQr = () => {
    const a = document.createElement("a");
    a.href = qr.current.querySelector("canvas").toDataURL("image/png");
    a.download = `${book.slug}-qr.png`;
    a.click();
  };

  const s = book.settings || {};
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl border-0 neu-panel" data-testid="publish-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">Publish “{book.title}”</DialogTitle>
          <DialogDescription className="text-xs text-[#64748B]">Set access, publish an immutable version and share it anywhere.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <SelectField label="Access" value={s.visibility || "public"} onChange={(v) => saveSettings({ visibility: v })} options={[["public", "Public"], ["unlisted", "Unlisted"], ["password", "Password protected"]]} testid="publish-visibility" />
          <SelectField label="Default viewer" value={s.default_mode || "flip"} onChange={(v) => saveSettings({ default_mode: v })} options={[["flip", "Classic Book (page turn)"], ["fast", "Fast Reader (instant)"]]} testid="publish-default-mode" />
          {s.visibility === "password" && <div className="col-span-2"><TextField label="Password" value={s.password} onChange={(v) => onBookChange({ settings: { ...s, password: v } })} testid="publish-password" /><button onClick={() => saveSettings({ password: s.password })} className="mt-1 text-[11px] text-[#3D3DF0]" data-testid="publish-password-save">Save password</button></div>}
        </div>
        <button onClick={publish} disabled={busy} data-testid="publish-confirm-btn" className="flex items-center justify-center gap-2 rounded-full neu-btn-primary py-3 text-sm font-medium disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
          {book.published_version ? `Publish new version (v1.${book.published_version})` : "Publish version 1.0"}
        </button>

        {book.published_version ? (
          <Tabs defaultValue="link" className="mt-2">
            <TabsList className="h-auto gap-5 bg-transparent p-0"><Tab v="link">Direct URL</Tab><Tab v="embed">iframe</Tab><Tab v="wix">Wix element</Tab><Tab v="qr">QR code</Tab><Tab v="versions">Versions</Tab></TabsList>
            <TabsContent value="link" className="space-y-2">
              <CodeBox code={shareUrl} testid="publish-url" />
              <p className="text-[11px] text-[#64748B]">Shows your book’s cover and title when shared on WhatsApp, Facebook, X, LinkedIn and Slack. Opens the reader instantly.</p>
              <a href={readUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#3D3DF0]" data-testid="publish-open-reader"><ExternalLink className="h-3 w-3" />Open reader</a>
            </TabsContent>
            <TabsContent value="embed" className="space-y-2">
              <p className="text-xs text-[#64748B]">Compatibility mode — works in Wix (Embed → HTML iframe), WordPress, Squarespace and any custom site.</p>
              <CodeBox code={embed} testid="publish-iframe" />
            </TabsContent>
            <TabsContent value="wix" className="space-y-2">
              <p className="text-xs text-[#64748B]">Optimized Wix mode — in Wix Editor add Embed Code → Custom Element, set Server URL to the script below and tag name <span className="font-mono text-[#0F172A]">ibookstudio-book</span>, attribute <span className="font-mono text-[#0F172A]">book="{book.slug}"</span>. Or paste this snippet into any site.</p>
              <CodeBox code={wix} testid="publish-wix" />
            </TabsContent>
            <TabsContent value="qr" className="flex items-center gap-6">
              <div ref={qr} className="rounded-md bg-white p-3" data-testid="publish-qr"><QRCodeCanvas value={shareUrl} size={148} /></div>
              <button onClick={downloadQr} className="flex items-center gap-2 rounded-full neu-btn px-4 py-2 text-xs" data-testid="publish-qr-download"><Download className="h-3.5 w-3.5" />Download PNG</button>
            </TabsContent>
            <TabsContent value="versions">
              <ul className="space-y-1 font-mono text-xs" data-testid="publish-versions">
                {versions.map((v) => (
                  <li key={v.version} className="flex justify-between rounded px-2 py-1.5 odd:bg-[#EEF2F8]"><span>v{v.label}{v.version === book.published_version && <span className="ml-2 text-[#5FA82E]">LIVE</span>}</span><span className="text-[#64748B]">{new Date(v.published_at).toLocaleString()}</span></li>
                ))}
              </ul>
            </TabsContent>
          </Tabs>
        ) : (
          <p className="text-center text-xs text-[#64748B]">Publishing creates an immutable version. Your live URL always points to the latest version.</p>
        )}
      </DialogContent>
    </Dialog>
  );
};
